import { useEffect, useState } from 'react';
import { Users, TrendingUp, DollarSign, Activity, Search } from 'lucide-react';
import { format } from 'date-fns';
import api from '../utils/api';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const AdminInvestorsOverview = () => {
  const [investors, setInvestors] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchInvestorsOverview();
  }, []);

  const fetchInvestorsOverview = async () => {
    try {
      const response = await api.get('/admin/investors-overview');
      setInvestors(response.data.investors);
      setSummary(response.data.summary);
    } catch (error) {
      toast.error('Failed to load investors overview');
    } finally {
      setLoading(false);
    }
  };

  const filteredInvestors = investors.filter(investor =>
    investor.display_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    investor.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-64"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-200 rounded"></div>
            ))}
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Investors Overview</h1>
            <p className="text-slate-600 mt-1">Consolidated view of all investors and their performance</p>
          </div>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-sm text-slate-600">Total Investors</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">{summary.total_investors}</div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-green-100 rounded-lg">
                  <DollarSign className="w-5 h-5 text-green-600" />
                </div>
                <span className="text-sm text-slate-600">Total Invested</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">
                ${summary.total_invested.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-sm text-slate-600">Total Gain</span>
              </div>
              <div className={`text-3xl font-bold ${summary.total_gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {summary.total_gain >= 0 ? '+' : ''}${summary.total_gain.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
              <div className="flex items-center space-x-3 mb-2">
                <div className="p-2 bg-orange-100 rounded-lg">
                  <Activity className="w-5 h-5 text-orange-600" />
                </div>
                <span className="text-sm text-slate-600">Total Investments</span>
              </div>
              <div className="text-3xl font-bold text-slate-900">{summary.total_investments}</div>
              <div className="text-sm text-slate-600 mt-1">{summary.active_investments} active</div>
            </div>
          </div>
        )}

        {/* Search Bar */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search investors by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Investors Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Investor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Phone</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase">Account Value</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase">Total Gain</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase">ROI %</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-600 uppercase">Annual Return %</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase">Investments</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase">Transactions</th>
                <th className="px-6 py-3 text-center text-xs font-medium text-slate-600 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Joined</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredInvestors.map((investor) => (
                <tr key={investor.id} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="font-medium text-slate-900">{investor.display_name}</div>
                  </td>
                  <td className="px-6 py-4 text-slate-600">{investor.email}</td>
                  <td className="px-6 py-4 text-slate-600">{investor.phone_number || 'N/A'}</td>
                  <td className="px-6 py-4 text-right font-semibold text-slate-900">
                    ${investor.account_value.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`px-6 py-4 text-right font-semibold ${investor.total_gain >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {investor.total_gain >= 0 ? '+' : ''}${investor.total_gain.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`px-6 py-4 text-right font-semibold ${(investor.roi_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(investor.roi_pct || 0) >= 0 ? '+' : ''}{(investor.roi_pct || 0).toFixed(2)}%
                  </td>
                  <td className={`px-6 py-4 text-right font-semibold ${(investor.annual_return_pct || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {(investor.annual_return_pct || 0) >= 0 ? '+' : ''}{(investor.annual_return_pct || 0).toFixed(2)}%
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {investor.total_investments} ({investor.active_investments} active)
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800">
                      {investor.total_transactions ? investor.total_transactions.toLocaleString() : '0'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      investor.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {investor.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600">
                    {investor.first_deposit_date ? format(new Date(investor.first_deposit_date), 'MMM d, yyyy') : 
                     investor.created_at ? format(new Date(investor.created_at), 'MMM d, yyyy') : 'N/A'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredInvestors.length === 0 && (
            <div className="p-12 text-center text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-20" />
              <p className="text-lg font-medium">No investors found</p>
              <p className="text-sm mt-1">Try adjusting your search criteria</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminInvestorsOverview;
